import { Snackbar } from "@mui/material";
import axios from "axios";
import mapboxgl, { GeolocateControl, Map, Marker } from "mapbox-gl";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { URLSearchParams } from "url";
import '../component/Annimation.scss';
import { Header } from "../component/Header";
import { Loader } from "../component/Loader";
import { useMobile } from "../context/MobileContext";
import { defaultLat, defaultLng, EVENT_MAP_MOVED, sendTapEvent } from "../helper/helper";
import { initPriceLayer, priceVisibility, updateSource } from "../helper/mapDataFeed";
import { buildMillPrices, MillPriceOnMap, toPriceFeatureCollection } from "../model/MillPriceOnMap";
import { PriceModel } from "../model/PriceModel";
import { colorAccent, colorBorder, colorForeground, colorNavBgSolid } from "../rsc/colors";
import PickupMarker from "../rsc/pickup.svg";
import { LoadingPricePage } from "./LoadingPricePage";
import MapBox from "./MapBox";
import { PagePriceMillAccepted } from "./PagePriceMillAccepted";
import { PagePriceMillSelected } from "./PagePriceMillSelected";

export type STEP_TYPE =
    | "home"
    | "price_mill_selected"
    | "price_mill_accepted"
    | "price_farm_no_price"
    | "price_farm_received"
    | "pickup";

export type NavigationState = {
    step: STEP_TYPE
    nafPrice?: number
    timestamp?: number
}

// The FeatureCollection handed to the mapbox 'prices' source. Properties are a MillPriceOnMap plus
// the four styling keys getPriceSource() injects client-side.
export type FeaturePriceMap = {
    type: string
    features: Array<{
        type: string
        geometry: {
            type: string,
            coordinates: [number, number]
        },
        properties: MillPriceOnMap & {
            price: number,
            icon: string,
            textColor: string,
            sortOrder: number
        }
    }>
}

export type Param = {
    key: string
    value: any
}

export function MainPage() {

    const { t } = useTranslation();

    const [param] = useSearchParams()

    const initParam = useRef<URLSearchParams>(cleanInitParam(param))

    const initOrg = useRef<string>(param.get('mill'))

    const location = useLocation();

    const didDraggedMap = useRef<boolean>(false)

    const instructionRef = useRef<HTMLDivElement>(null)
    const [priceSelected, setPriceSelected] = useState<PriceModel | undefined>(undefined)
    const [jsonPrices, setJsonPrices] = useState<FeaturePriceMap | undefined>(undefined)
    const mapRef = useRef<Map | undefined>(undefined)
    const [instructionType, setInstructionType] = useState<STEP_TYPE>("home")
    const posControl = useRef<GeolocateControl | undefined>(undefined)

    const [nafPrice, setNafPrice] = useState<number | undefined>(undefined)
    const [millPriceAccepted, setMillPriceAccepted] = useState<number | undefined>(undefined)

    const userPos = useRef<any | undefined>(undefined)

    // eslint-disable-next-line
    const pickupPos = useRef<any | undefined>(undefined)
    const cameraPos = useRef<any | undefined>({ lng: defaultLng, lat: defaultLat })

    const markerRef = useRef<Marker | undefined>(undefined)

    const [loadingPrice, setLoadingPrice] = useState(false)

    const [error, setError] = useState<string | undefined>(undefined)

    const [askPosLoader, setAskPosLoader] = useState<boolean>(false)

    const [noPriceFound, setNoPriceFound] = useState<boolean>(false)

    const isMobile = useMobile();

    const navigate = useNavigate();

    const allPrices = useMemo(() => jsonPrices ? jsonPrices.features.map(f => PriceModel.buildFromFeature(f.properties, f.geometry)) : [], [jsonPrices])

    function cleanInitParam(params: URLSearchParams): URLSearchParams {
        params.delete('id')
        params.delete('mill_name')
        params.delete('subdistrict')
        params.delete('price')
        params.delete('village')
        params.delete('qbp')
        params.delete('mill_id')
        params.delete('program_name')
        return params
    }

    useEffect(() => {
        if (initOrg && jsonPrices) {
            const priceFound = jsonPrices.features.find(o => o.properties.mill_id === (parseInt(initOrg.current ?? '0')))
            if (priceFound) {
                const pp = priceFound.properties
                const price = PriceModel.buildFromFeature(pp, priceFound.geometry)
                const exParams = price.toParam()
                setPriceSelected(price)
                const customState: NavigationState = { step: "price_mill_selected", timestamp: Date.now() };
                nextStep('price_mill_selected', customState, exParams)
            }
        }
        // eslint-disable-next-line
    }, [jsonPrices])

    function fetchNafPrice() {
        setLoadingPrice(true)
        axios.get(`${process.env.REACT_APP_BASE_URL_PUBLIC}/mapTilingService/bidByPos/${pickupPos.current.lng}/${pickupPos.current.lat}`, {
            withCredentials: false,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        }).then((data: any) => {
            const price = data.data.price
            if (price) {
                onFarmPriceReceived(price)
            } else {
                const customState: NavigationState = { step: "price_farm_no_price" }
                nextStep('price_farm_no_price', customState)
                setNoPriceFound(true)
            }
        }).catch(e => {
            if (e?.message) {
                setError(e?.message)
            } else {
                setError(t('error_generic') as string)
            }
        })
            .finally(() => {
                setLoadingPrice(false)
            })
    }

    function fetchAllPrices() {
        axios.get(`${process.env.REACT_APP_BASE_URL_PUBLIC}/mapTilingService/prices`, {
            withCredentials: false,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }
        }).then((data: any) => {
            const prices = buildMillPrices(data.data?.prices)
            if (prices.length) {
                setJsonPrices(toPriceFeatureCollection(prices))
            } else {
                setError(t('error_no_prices_available') as string)
            }
        }).catch(e => {
            if (e?.message) {
                setError(e?.message)
            } else {
                setError(t('error_generic') as string)
            }
        })
            .finally(() => {
                // setLoading(false)
            })
    }

    function handleVisibility() {
        const path = location.pathname.split('/').pop() as STEP_TYPE
        updateInstruction(path)
        if (path !== "pickup") {
            if (path === 'home') {
                setPriceSelected(undefined)
            }
            hidePickup()
            if (mapRef.current) {
                priceVisibility(mapRef.current, true)
            }
        } else {
            if (markerRef.current) {
                showPickup()
            } else {
                setPickUp()
            }
            if (mapRef.current) {
                priceVisibility(mapRef.current, false)
            }
        }
    }

    function mapLoaded(map: Map) {
        mapRef.current = map;
        initPriceLayer(mapRef.current, jsonPrices, (p: any, g: any) => {
            const price = PriceModel.buildFromFeature(p, g)
            const exParams = price.toParam()
            setPriceSelected(price)
            const customState: NavigationState = { step: "price_mill_selected", timestamp: Date.now() };
            nextStep('price_mill_selected', customState, exParams)
        }, () => {
            fetchAllPrices()
            handleVisibility()
        })
        mapRef.current.on('dragend', () => {
            if (!didDraggedMap.current) {
                sendTapEvent(EVENT_MAP_MOVED)
                didDraggedMap.current = true
            }
            const center = map.getCenter(); // returns a LngLat object
            cameraPos.current = { lng: center.lng, lat: center.lat }
        });
    }

    useEffect(() => {
        if (jsonPrices) {
            updateSource(mapRef.current!, jsonPrices, priceSelected?.id)
        }
        if (priceSelected) {
            flyTo([priceSelected.coordinates!.at(0)!, priceSelected.coordinates!.at(1)!])
        }
        // eslint-disable-next-line
    }, [jsonPrices, priceSelected])

    function setPickUp(byPassUserPos: boolean = false) {
        if ((userPos.current || byPassUserPos) && mapRef.current) {
            priceVisibility(mapRef.current, false)
            addPickUpMarker()

            const customState: NavigationState = { step: "pickup", timestamp: Date.now() };
            nextStep('pickup', customState)
        } else {
            askForPosition(() => {
                setPickUp()
                flyToUserPos()
            }, () => {
                setPickUp(true)
            })
        }
    }

    const flyTo = (coord: number[]) => {
        mapRef.current?.flyTo({
            center: [coord[0], coord[1]],
            essential: true // this animation is considered essential with respect to prefers-reduced-motion
        });
    }

    function flyToUserPos() {
        const ppos = getPickupPos()
        flyTo([ppos.lng, ppos.lat])
    }

    function askForPosition(onSuccess?: () => void, onError?: () => void) {
        if (!navigator.geolocation) {
            setError(t('error_geolocation_not_supported') as string);
        } else if (mapRef.current) {
            setAskPosLoader(true)
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setAskPosLoader(false)
                    const { latitude, longitude } = position.coords;
                    userPos.current = { latitude, longitude }
                    const pickupPos = getPickupPos()
                    const control = new mapboxgl.GeolocateControl({
                        positionOptions: { enableHighAccuracy: true },
                        trackUserLocation: true,
                        showUserLocation: true,
                        showAccuracyCircle: false,
                        fitBoundsOptions: { center: [pickupPos.lng, pickupPos.lat], zoom: mapRef.current?.getZoom() }
                    })
                    control.on('error', (e: any) => {
                        console.log('controle failure', error)
                    })
                    control.on('geolocate', (e: any) => {
                        console.log('controle success', error)
                    })
                    if (!posControl.current) {
                        posControl.current = control
                        mapRef.current?.addControl(control);
                        setTimeout(() => {
                            control.trigger()
                        }, 1000)
                    } else {
                        setTimeout(() => {
                            posControl.current?.trigger()
                        }, 1000)
                    }
                    onSuccess?.()
                },
                (err) => {
                    setError(err.message);
                    setAskPosLoader(false)
                    onError?.()
                }
                , {
                    timeout: 10000,
                    maximumAge: 60000
                });
        }
    }

    useEffect(() => {
        if (!nafPrice) {
            setLoadingPrice(false)
        }
        // eslint-disable-next-line
    }, [nafPrice])

    function addPickUpMarker() {
        if (markerRef.current) {
            showPickup()
        } else {
            const customMarker = document.createElement('div');
            const markerImg = document.createElement('img');
            markerImg.src = PickupMarker;
            markerImg.style.width = "170px"
            markerImg.style.width = "85px"

            // Append the image to the custom marker
            customMarker.appendChild(markerImg);
            customMarker.style.zIndex = "8"
            pickupPos.current = getPickupPos()
            const marker = new mapboxgl.Marker({
                element: customMarker,
                draggable: true,
                anchor: 'bottom'
            })
                .setLngLat([pickupPos.current.lng, pickupPos.current.lat])
                .addTo(mapRef.current!);

            marker.on('dragend', () => {
                const lngLat = marker.getLngLat();
                pickupPos.current = { lng: lngLat.lng, lat: lngLat.lat }
            });
            markerRef.current = marker
        }
    }

    function getPickupPos() {
        return pickupPos.current ?? { lng: userPos?.current?.longitude ?? defaultLng, lat: userPos?.current?.latitude ?? defaultLat }
    }

    function nextStep(step: STEP_TYPE, state?: NavigationState, extraParam?: Param[]) {
        var newParam: string = ''
        initParam.current.forEach((value: string, key: string) => {
            if (newParam.length > 0) {
                newParam += '&'
            }
            newParam += `${key}=${value}`
        })
        extraParam?.forEach((p: Param) => {
            if (newParam.length > 0) {
                newParam += '&'
            }
            newParam += `${p.key}=${p.value}`
        })
        if (newParam) {
            newParam = '?' + newParam
        }
        updateInstruction(step)
        if (newParam.length > 0) {
            navigate(`/${step}${newParam}`, { state: state });
        } else {
            navigate(`/${step}`, { state: state });
        }
    }

    function showPickup() {
        if (markerRef.current) {
            pickupPos.current = getPickupPos()
            markerRef.current.setLngLat([pickupPos.current.lng, pickupPos.current.lat])
            markerRef.current!.getElement().style.display = "block"
        }
    }

    function hidePickup() {
        if (markerRef.current) {
            markerRef.current!.getElement().style.display = "none"
        }
    }

    useEffect(() => {
        handleVisibility()
        // eslint-disable-next-line
    }, [location]);


    function updateInstruction(step: STEP_TYPE) {
        if (step === 'pickup' || step === 'price_farm_received') {
            setInstructionType('pickup')
        } else {
            setInstructionType('home')
        }
    }

    function onMillPriceAccepted(price?: number) {
        const customState: NavigationState = { step: "price_mill_accepted" }
        nextStep('price_mill_accepted', customState)
        setMillPriceAccepted(price)
    }

    function onFarmPriceReceived(price: number) {
        const customState: NavigationState = { step: "price_farm_received" }
        nextStep('price_farm_received', customState)
        setNafPrice(price)
        setLoadingPrice(false)
    }

    return (<div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
            <Header hasPrices={allPrices.length > 0} />
            {/* <Instruction ref={instructionRef} type={instructionType} askForLocation={() => askForPosition()} /> */}
            <div id="Map" style={{ width: "100%", flex: 1, position: "relative", display: "flex", flexDirection: "column" }}>
                <MapBox initZoom={9} style={{ width: "100%", flexDirection: "column", display: "flex", flex: 1 }} onLoaded={(map: Map) => { mapLoaded(map) }} />
                {/* Floating CTA buttons — fixed to right side over the map */}
                <div style={{
                    position: "fixed",
                    top: isMobile ? 96 : 80,
                    right: 16,
                    zIndex: 100,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 10,
                }}>
                    {/* WhatsApp */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <a href="https://wa.me/6281944222166" target="_blank" rel="noopener noreferrer" style={{
                            display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", width: 144,
                            background: "hsl(142,70%,41%)", color: "#fff", borderRadius: 999,
                            fontFamily: "'Public Sans', system-ui, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                            textDecoration: "none", whiteSpace: "nowrap",
                        }}>
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16, flexShrink: 0 }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                            WhatsApp
                        </a>
                    </div>
                    {/* Website */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <a href="https://pempem.io" target="_blank" rel="noopener noreferrer" style={{
                            display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", width: 144,
                            background: colorAccent, color: "#fff", borderRadius: 999,
                            fontFamily: "'Public Sans', system-ui, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                            textDecoration: "none", whiteSpace: "nowrap",
                        }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 16, height: 16, flexShrink: 0 }}><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
                            Website
                        </a>
                    </div>
                    {/* TikTok */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <a href="https://www.tiktok.com/@pempem_id" target="_blank" rel="noopener noreferrer" style={{
                            display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", width: 144,
                            background: colorNavBgSolid, color: colorForeground, border: `1px solid ${colorBorder}`, borderRadius: 999,
                            fontFamily: "'Public Sans', system-ui, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                            textDecoration: "none", whiteSpace: "nowrap",
                        }}>
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16, flexShrink: 0 }}><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.75a8.18 8.18 0 004.76 1.52V6.84a4.84 4.84 0 01-1-.15z" /></svg>
                            TikTok
                        </a>
                    </div>
                    {/* Google Play */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <a href="https://play.google.com/store/apps/details?id=com.pemPem&hl=en" target="_blank" rel="noopener noreferrer" style={{
                            display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 16px", width: 144,
                            background: colorNavBgSolid, color: colorForeground, border: `1px solid ${colorBorder}`, borderRadius: 999,
                            fontFamily: "'Public Sans', system-ui, sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                            textDecoration: "none", whiteSpace: "nowrap",
                        }}>
                            <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 16, height: 16, flexShrink: 0 }}><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302L15.396 12l2.302-2.492zM5.864 2.658L16.8 9.991l-2.302 2.302L5.864 3.658v-1z" /></svg>
                            Google Play
                        </a>
                    </div>
                </div>
            </div>
            <Routes>
                {/* <Route path="home" element={
                    <PagePriceAtMill page="mill_prices_map" setPickUp={setPickUp} />
                } /> */}
                <Route path="price_mill_selected" element={
                    <PagePriceMillSelected setPickUp={setPickUp} page="price_mill_selected" onMillPriceAccepted={(p: PriceModel) => onMillPriceAccepted(p.pricePerUnit)} priceSelected={priceSelected} setPriceSelected={(price: any) => {
                        if (!price) {
                            nextStep('home')
                        }
                        setPriceSelected(price)
                    }} fetchedPrices={allPrices} />
                } />
                <Route path="price_mill_accepted" element={
                    <PagePriceMillAccepted page="price_mill_accepted" millPriceAccepted={millPriceAccepted} />
                } />
                {/* <Route path="price_farm_no_price" element={
                    <PageFarmNoPrice page="price_farm_no_price" setNoPriceFound={setNoPriceFound} noPriceFound={noPriceFound} />
                } />
                <Route path="price_farm_received" element={
                    <PagePriceFarm page="price_farm_received" nafPrice={nafPrice} />
                } />
                <Route path="pickup" element={
                    <PagePickUp page="pickup" fetchNafPrice={fetchNafPrice} />
                } /> */}
            </Routes>
            {
                loadingPrice &&
                <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, zIndex: "9", backgroundColor: "#fff", display: "flex", justifyContent: "center" }}>
                    <LoadingPricePage />
                </div>
            }
            <Loader isLoading={askPosLoader} />
            {
                error ?
                    <Snackbar
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "center"
                        }}
                        open={error !== undefined}
                        autoHideDuration={6000}
                        onClose={() => { setError(undefined) }}
                        message={error}
                        sx={{
                            '& .MuiSnackbarContent-root': {
                                backgroundColor: "#f00",
                                fontWeight: "600"
                            }
                        }}
                    /> : <></>
            }

        </div>
    </div >)
}